import React, {useState, useEffect} from 'react';
import {
    Modal,
    Box,
    Button,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Checkbox,
    TextField
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import PlanWizardWelcomeStep from "../plan-wizard-welcome-step";
import PlanWizardPreferencesStep from "../PlanWizardPreferencesStep";
import {useCookies} from "react-cookie";
import {getFuturePlanWizardSettings} from "../../shared/api";
import {useSelector} from "react-redux";

const PlanAutomationLoadingModal = (props) => {
    return (
        <Modal open={props.open} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
            <Box sx={{ width: 500, margin: 'auto', marginTop: '25%', textAlign: 'center', backgroundColor: 'white', border: '2px solid #000', p: 2 }}>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                    Automating Future Adjustment Entries…
                </Typography>
            </Box>
        </Modal>
    );
};


const PlanAutomationCompletedModal = (props) => {
    return (
        <Modal open={props.open} aria-labelledby="completed-modal-title" aria-describedby="completed-modal-description">
            <Box sx={{ width: 500, margin: 'auto', marginTop: '25%', textAlign: 'center', backgroundColor: 'white', border: '2px solid #000', p: 2 }}>
                <Typography id="completed-modal-title" variant="h6" component="h2">
                    Completed! Entries created in Future Adjustment Section
                </Typography>
                <Button onClick={props.handleModalClosed} color="primary" variant="contained" style={{ marginTop: '10px' }}>
                    OK
                </Button>
            </Box>
        </Modal>
    );
};
const PlanAutomationWizard = ({ clientId, open, handleClose, runWizard }) => {

    const [wizardStep, setWizardStep] = useState(0);

    const [state, setState] = useState({
        id: null,
        stability: true,
        accumulation: false,
        balanced: false,
        annuitiesAllocation: 50,
        balancedAllocation: 50,
    });

    const [cookies, setCookie, removeCookie] = useCookies(['hideAtStartUp']);

    const [isLoading, setIsLoading] = useState(true);

    const [isCalculating, setIsCalculating] = useState(false);

    const [isCompleted, setIsCompleted] = useState(false);

    const [confirmDialog, setConfirmDialog] = useState(false);

    const [exitDialog, setExitDialog] = useState(false);

    const token = useSelector((state) => state.dataStore.getIn(["loginInfo", "authorizationToken"]));
    const loadPlanWizardSettings = async () => {
        const results = await getFuturePlanWizardSettings(clientId, token);
        setState({
            id: results.id,
            stability: results.planOptimizationType == null || results.planOptimizationType === "STABILITY",
            balanced: results.planOptimizationType === "BALANCED",
            accumulation: results.planOptimizationType === "ACCUMULATION",
            annuitiesAllocation: results.annuityAllocation ? results.annuityAllocation : 50,
            balancedAllocation: results.balanced ? results.balanced : 50,
        });
        setIsLoading(false);
        if (cookies.hideAtStartUp != true) setWizardStep(0);
    };

    useEffect(() => {
        loadPlanWizardSettings();
    }, []);

    const handleShowAtStartupChange = (event) => {
        if (event.target.checked) {
            removeCookie("hideAtStartUp");
            return;
        }
        setCookie("hideAtStartUp", true);
    }

    const handleChange = (event) => {
        setState({
            ...state,
            stability: false,
            accumulation: false,
            balanced: false,
            [event.target.name]: event.target.checked
        });
    };

    const handleSliderChange = (event, newValue) => {
        setState({ ...state, annuitiesAllocation: newValue});
    };

    const handleAnnuityTextBoxChange = (event) => {
        const newValue = parseInt(event.target.value);
        if (newValue < 0 || newValue > 100) return;

        setState({
            ...state,
            annuitiesAllocation: newValue
        });
    }

    const handleBalancedSliderChange = (event, newValue) => {
        setState({
            ...state,
            balancedAllocation: newValue
        });
    };

    const handleBalancedLowerBoundsTextboxChange = (event) => {
        const v = parseFloat(event.target.value);

        if (v < 0) {
            setState({
                ...state,
                balancedAllocation: 0
            });
            return;
        }

        if (v > 100) {
            setState({
                ...state,
                balancedAllocation: 100
            });
            return;
        }

        setState({
            ...state,
            balancedAllocation: event.target.value
        })
    };

    const handleBalancedUpperBoundsTextboxChange = (event) => {
      const v = parseFloat(event.target.value);

      if (isNaN(v)) {
          setState({
              ...state,
              balancedAllocation: 100.0
          });
          return;
      }

      setState({
          ...state,
          balancedAllocation: 100.0 - Math.min(v, 100)
      });
    };

    const run = async () => {
        setConfirmDialog(false);
        setIsCalculating(true);
        await runWizard(state);
        setIsCalculating(false);
        setIsCompleted(true);
    }

    const handleNextStep = async () => {
        if (wizardStep === 1) {
            setConfirmDialog(true);
            return;
        }
        setWizardStep(wizardStep + 1);
    }

    const handleCloseWizardCompleteModal = () => {
        setIsCompleted(false);
        handleClose();
    }

    const handleConfirmDialogClose = () => {
        setConfirmDialog(false);
    }

    const handleCloseWizard = () => {
        setExitDialog(true);
    }

    const handleExitDialogClose = () => {
        setExitDialog(false);
        handleClose();
    }

    const handleExitDialogStay = () => {
        setExitDialog(false);
    }

    let confirmDialogComponent;
    if (confirmDialog) {
        confirmDialogComponent = (
            <Dialog
                fullWidth={true}
                maxWidth={"sm"}
                open={true}
                onClose={handleConfirmDialogClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <DialogTitle>{"Plan Automation - Confirm"}</DialogTitle>
                <DialogContent style={{ margin: "auto" }}>
                    <Typography variant="subtitle1">
                        Proceed with Plan Automation? Any previous “Future Adjustment” entries would be replaced by the Plan Automation Wizard.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button className={"cancel-button"} onClick={handleConfirmDialogClose}>Cancel</Button>
                    <Button onClick={run}>OK</Button>
                </DialogActions>
            </Dialog>
        );
    }

    let exitDialogComponent;
    if (exitDialog) {
        exitDialogComponent = (
            <Dialog
                fullWidth={true}
                maxWidth={"sm"}
                open={true}
                onClose={handleExitDialogStay}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <DialogTitle>{"Exit Plan Automation Wizard?"}</DialogTitle>
                <DialogActions>
                    <Button className={"cancel-button"} onClick={handleExitDialogStay}>No</Button>
                    <Button onClick={handleExitDialogClose}>Yes</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <div>
            <Dialog
                fullWidth={true}
                maxWidth={"lg"}
                open={open}
                onClose={handleCloseWizard}
                className={"plan-automation-wizard-modal"}>
                    <DialogTitle>Plan Automation Wizard  <Button className={"plan-automation-wizard-cancel-btn"} variant="text" startIcon={<CancelIcon />} onClick={handleCloseWizard} color="primary" />
                    </DialogTitle>
                    <DialogContent>
                        {isLoading && <Typography>Please wait...</Typography>}
                        {wizardStep === 0 && !isLoading && <PlanWizardWelcomeStep skip={cookies.hideAtStartUp} handleNextStep={handleNextStep} />}
                        {wizardStep === 1 && !isLoading && <PlanWizardPreferencesStep
                            state={state}
                            handleChange={handleChange}
                            handleSliderChange={handleSliderChange}
                            handleAnnuityTextBoxChange={handleAnnuityTextBoxChange}
                            handleBalancedSliderChange={handleBalancedSliderChange}
                            handleBalancedLowerBoundsTextboxChange={handleBalancedLowerBoundsTextboxChange}
                            handleBalancedUpperBoundsTextboxChange={handleBalancedUpperBoundsTextboxChange}
                        />}
                    </DialogContent>
                <DialogActions className={"plan-automation-wizard-actions"}>
                    <div>
                        <FormControlLabel
                            className={"show-at-startup-label"}
                            control={
                                <Checkbox
                                    checked={!cookies.hideAtStartUp}
                                    onChange={handleShowAtStartupChange}
                                    name="showAtStartup"
                                    color="primary"
                                />
                            }
                            label="Show at startup"
                        />
                    </div>

                    <Button onClick={handleNextStep} color="primary">
                        {wizardStep === 0 ? 'Next' : 'Run'}
                    </Button>
                    {confirmDialogComponent}
                    {exitDialogComponent}
                </DialogActions>
            </Dialog>
            <PlanAutomationLoadingModal open={isCalculating} />
            <PlanAutomationCompletedModal open={isCompleted} handleModalClosed={handleCloseWizardCompleteModal} />
        </div>
    );
};

export default PlanAutomationWizard;
